import React, { useEffect, useState, useCallback } from 'react';
import {
  getSlackChannelConfigurations,
  createSlackChannelConfiguration,
  updateSlackChannelConfiguration,
  deleteSlackChannelConfiguration,
} from '../../services/SlackChannelConfigurationsService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../../Footer';

function SlackChannelConfigurations() {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    channelId: '',
    channelName: '',
    ccEmails: '',
    isActive: true,
  });

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchConfigurations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSlackChannelConfigurations();
      setConfigurations(data || []);
    } catch (err) {
      setError('Failed to load slack channel configurations');
      toast.error('Failed to load slack channel configurations');
      setConfigurations([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const resetForm = () => {
    setFormData({
      channelId: '',
      channelName: '',
      ccEmails: '',
      isActive: true,
    });
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingConfig(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (config) => {
    setModalMode('edit');
    setEditingConfig(config);
    setFormData({
      channelId: config.channelId || '',
      channelName: config.channelName || '',
      ccEmails: config.ccEmails || '',
      isActive: config.isActive ?? true,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.channelId.trim()) {
      toast.warning('Channel ID is required');
      return;
    }
    if (!formData.channelName.trim()) {
      toast.warning('Channel Name is required');
      return;
    }

    setSaving(true);
    try {
      if (modalMode === 'create') {
        await createSlackChannelConfiguration(formData);
        toast.success('Channel configuration created successfully');
      } else {
        await updateSlackChannelConfiguration(editingConfig.id, formData);
        toast.success('Channel configuration updated successfully');
      }
      closeModal();
      fetchConfigurations();
    } catch (err) {
      toast.error(`Failed to ${modalMode === 'create' ? 'create' : 'update'} channel configuration`);
    }
    setSaving(false);
  };

  const openDeleteModal = (config) => {
    setDeletingConfig(config);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingConfig(null);
  };

  const handleDelete = async () => {
    if (!deletingConfig) return;

    setDeleting(true);
    try {
      await deleteSlackChannelConfiguration(deletingConfig.id);
      toast.success('Channel configuration deleted successfully');
      closeDeleteModal();
      fetchConfigurations();
    } catch (err) {
      toast.error('Failed to delete channel configuration');
    }
    setDeleting(false);
  };

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
      <ToastContainer />

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Slack Channel Configurations</h4>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-primary d-flex align-items-center"
            onClick={openCreateModal}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Add Channel
          </button>
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={fetchConfigurations}
            disabled={loading}
            title="Refresh"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise me-2"></i>
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {loading && configurations.length === 0 ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Channel Name</th>
                <th>Channel ID</th>
                <th>CC Emails</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configurations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No channel configurations found
                  </td>
                </tr>
              ) : (
                configurations.map((config) => (
                  <tr key={config.id}>
                    <td>
                      <span className="fw-semibold">{config.channelName}</span>
                    </td>
                    <td>
                      <code className="small">{config.channelId}</code>
                    </td>
                    <td>
                      <span className="small text-muted">
                        {config.ccEmails || '-'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${config.isActive ? 'bg-success' : 'bg-secondary'}`}
                      >
                        {config.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="small text-muted">
                      {config.createdAt ? new Date(config.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openEditModal(config)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openDeleteModal(config)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeModal();
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === 'create' ? 'Add Channel Configuration' : 'Edit Channel Configuration'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Channel ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="channelId"
                        value={formData.channelId}
                        onChange={handleInputChange}
                        placeholder="e.g., C0XXXXXXXX"
                        required
                      />
                      <div className="form-text">The Slack channel ID (starts with C)</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Channel Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="channelName"
                        value={formData.channelName}
                        onChange={handleInputChange}
                        placeholder="e.g., Ops Support"
                        required
                      />
                      <div className="form-text">A friendly name for this channel</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">CC Emails</label>
                      <input
                        type="text"
                        className="form-control"
                        name="ccEmails"
                        value={formData.ccEmails}
                        onChange={handleInputChange}
                        placeholder="e.g., ops@bettertrucks.com, lead@bettertrucks.com"
                      />
                      <div className="form-text">Comma-separated email addresses to CC on notifications</div>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Active
                      </label>
                      <div className="form-text">Only active channels appear in dropdowns</div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        modalMode === 'create' ? 'Create' : 'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeDeleteModal();
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete Channel Configuration</h5>
                  <button type="button" className="btn-close" onClick={closeDeleteModal}></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete the channel configuration:</p>
                  <p className="fw-bold">{deletingConfig?.channelName}</p>
                  <p className="text-muted small">Channel ID: {deletingConfig?.channelId}</p>
                  <div className="alert alert-warning mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    This action cannot be undone.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeDeleteModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      <Footer />
    </div>
  );
}

export default SlackChannelConfigurations;
